const API_METHODS = {
  POST: 'POST',
  GET: 'GET',
};

const HEADERS = {
  CONTENT_TYPE: 'Content-Type',
  CONTENT_APPLICATION_JSON: 'application/json',
};

const BASE_URL = location.origin;
const healthPassDevStore = 'https://health-pass.myshopify.com';
const stykuProduction = 'https://healthpass.blackhill-6e293273.eastus.azurecontainerapps.io/api';
const stykuDevelopment = 'https://healthpassdev.calmpebble-397d4bfe.eastus.azurecontainerapps.io/api';

let STYKU_BASE_URL = stykuProduction;

if (BASE_URL === healthPassDevStore) {
  STYKU_BASE_URL = stykuDevelopment;
}

/**
 * Creates a standardized API payload for fetch calls.
 * Uses BASE_URL for Shopify (default) or STYKU_BASE_URL if useShopifyBase is false.
 * Adds Content-Type and optional Bearer token headers.
 *
 * @param {string} endpoint - API path (e.g., '/account/login')
 * @param {object} options - Config: method, contentType, accessToken, useShopifyBase, etc.
 * @returns {object} - Payload with full URL, method, headers, and extra options
 */
function createApiPayload(
  endpoint,
  {
    method = API_METHODS.POST,
    contentType = HEADERS.CONTENT_APPLICATION_JSON,
    useShopifyBase = true,
    ...otherOptions
  } = {}
) {
  const fullURL = `${useShopifyBase ? BASE_URL : STYKU_BASE_URL}${endpoint}`;

  const payload = {
    endPoint: fullURL,
    method,
    headers: {
      [HEADERS.CONTENT_TYPE]: contentType,
    },
    ...otherOptions,
  };

  return payload;
}

/**
 * Wrapper function for creating Shopify API payloads.
 * Ensures all Shopify-related requests use the BASE_URL (Shopify domain)
 * and passes through any additional configuration such as method, headers.
 *
 * @param {string} endpoint - The relative endpoint
 * @param {object} options - Additional fetch configuration (method, headers, etc.)
 * @returns {object} - Fully constructed API payload for use with fetch/request
 */
function shopifyApiPayload(endpoint, options = {}) {
  return createApiPayload(endpoint, {
    useShopifyBase: true,
    ...options,
  });
}

/**
 * Wrapper function for creating Styku backend API payloads.
 * Routes requests to the STYKU_BASE_URL (external backend) and includes token support.
 *
 * @param {string} endpoint - The relative endpoint (e.g., '/profile')
 * @param {object} options - Additional fetch configuration (method, headers, etc.)
 * @returns {object} - Fully constructed API payload for use with fetch/request
 */
function stykuApiPayload(endpoint, options = {}) {
  return createApiPayload(endpoint, {
    useShopifyBase: false,
    ...options,
  });
}

const STYKU_API = {
  SEND_EMAIL_VERIFICATION_CODE: stykuApiPayload('/v2/account/send-verification-code'),
  VERIFY_PROFILE_EMAIL: stykuApiPayload('/v2/account/email/verify-profile-email'),
  EMAIL_VERIFICATION_CODE: stykuApiPayload('/v2/account/verify-verification-code'),
  SECURE_ACCOUNT: stykuApiPayload('/v2/account/secure'),
  SCHEDULE_SCAN: stykuApiPayload('/v2/locations/schedule-scan'),
  AFFILIATE_PARTNER_LOCATIONS: (affiliateId) =>
    stykuApiPayload(`/v2/partners/${encodeURIComponent(affiliateId)}/locations`, {
      method: API_METHODS.GET,
    }),
};

const SHOPIFY_API = {
  CUSTOMER_LOGOUT: shopifyApiPayload('/account/logout', { method: API_METHODS.GET }),
  GET_CART_ITEM: shopifyApiPayload('/cart.js', { method: API_METHODS.GET }),
  ADD_ITEM_TO_CART: shopifyApiPayload('/cart/add.js'),
  UPDATE_CART_ITEM: shopifyApiPayload('/cart/update.js'),
  CHANGE_CART_ITEM: shopifyApiPayload('/cart/change.js'),
};

// Error status codes
const ERROR_CODES = Object.freeze({
  BAD_REQUEST: 400,
  GONE: 410, // Used when the verification code has expired
  EARLY_REQUEST: 425,
  MANY_REQUESTS: 429,
  SERVER_ERROR: 500,
});

const VERIFICATION_CODE = Object.freeze({
  PROFILE: {
    NOT_FOUND: 602,
    ALREADY_SECURED: 605,
    NOT_VERIFIED: 608,
    ALREADY_EXISTS: 686,
    PARTIALLY_VERIFIED: 733,
    DEMO_OR_APP_TESTER: 734,
    SECURE_FAILED: 617,
  },
  INVALID_EMAIL: 601,
  UNABLE_TO_SEND_EMAIL: 603,
  INVALID_VERIFICATION_CODE: 604,
  INVALID_PHONE_NUMBER: 724,
});

// Success status codes
const SUCCESS_CODE = Object.freeze({
  SUCCESS: 200,
});

const SESSION_KEY = {
  PROFILE: 'profile',
  RETURN_TO: 'return_to',
  CHECKOUT: 'checkout',
  AUTH_FLOW_STATE: 'auth_flow_state',
  VERIFICATION_METHOD: 'verification_method',
};

// Authentication flow states
const AUTH_FLOW_STATES = Object.freeze({
  INITIAL: 'initial',
  EMAIL_SUBMITTED: 'email_submitted',
  EMAIL_VERIFIED: 'email_verified',
  REGISTRATION_ALLOWED: 'registration_allowed',
});

const STORAGE_KEY = {
  REFERRAL_DATA: 'referral_data',
  LOCATION_ID: 'location_id',
  QR_CODE_ID: 'qr_code_id',
  REFERRER: 'referrer',
};

const PRODUCT_TYPE = {
  HEALTH_PASS: 'health-pass',
};

// Page URLs
// Market-aware navigation targets. Values come from window.themeRoutes, which Liquid renders
// per request (see snippets/scripts-and-styles.liquid), so each already carries the active
// market/locale prefix. The literal fallbacks only apply if the theme global is unavailable
// (e.g. this file loaded outside the theme layout) and are prefix-less primary-market paths.
const THEME_ROUTES = (typeof window !== 'undefined' && window.themeRoutes) || {};

const PAGE_URLS = {
  PRICING: THEME_ROUTES.pricing || '/pages/pricing',
  LOCATIONS: THEME_ROUTES.scanBooking || '/pages/locations',
  HEALTH_PRODUCT: THEME_ROUTES.healthProducts || '/collections/health-products',
  CART: THEME_ROUTES.cart || '/cart',
  ACCOUNT: THEME_ROUTES.account || '/account',
  SHOPIFY_LOGIN: THEME_ROUTES.accountLogin || '/account/login',
  SHOPIFY_REGISTER: THEME_ROUTES.accountRegister || '/account/register',
  // Multipass endpoint hangs off the market-aware account login path.
  MULTIPASS_LOGIN: (token) => `${BASE_URL}${THEME_ROUTES.accountLogin || '/account/login'}/multipass/${token}`,
};

/**
 * Normalizes a URL or path to a comparable pathname, preserving any market/locale prefix.
 * Use when comparing window.location.pathname against a PAGE_URLS value so the check holds
 * across markets (e.g. "/en-ca/pages/pricing"). Any trailing slash is stripped from both sides.
 *
 * @param {string} url - A full URL or root-relative path.
 * @returns {string} The normalized pathname (no trailing slash), or the input if unparseable.
 */
function toPathname(url) {
  try {
    // Security: BASE_URL is the storefront origin; only same-origin/relative paths are compared.
    const { pathname } = new URL(url, BASE_URL);
    return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  } catch (error) {
    return url;
  }
}

const ON_CHANGE_DEBOUNCE_TIMER = 300;
